"use client";

import {
    Button,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Typography } from "./wrappers/Typography";
import { useIsVariant } from "@/hooks/useVariant";

interface LicenseAgreementProps {
    open: boolean;
    onAgree: () => void;
    onCancel: () => void;
}

export const LicenseAgreement = (props: LicenseAgreementProps) => {
    const { open, onAgree, onCancel } = props;
    const { t } = useTranslation();

    const isCada = useIsVariant("cada");
    const licenseNamespace = isCada ? "cadaLicense" : "license";
    const studyLink = isCada
        ? { href: "https://www.cadaworkforcestudy.com", display: "www.cadaworkforcestudy.com" }
        : { href: "https://www.nadaworkforcestudy.com", display: "www.nadaworkforcestudy.com" };

    const getTKey = (key: string) => `${licenseNamespace}.${key}`;

    return (
        <Dialog maxWidth="lg" open={open}>
            <DialogTitle textAlign="center">
                <Typography component="div" variant="h7" fontWeight={500} tx={getTKey("title")} />
            </DialogTitle>
            <DialogContent
                sx={{
                    maxHeight: 527,
                    mx: 3,
                    mb: 3,
                    border: `1px solid rgba(0,0,0,.11)`,
                    p: 3,
                    py: "24px !important",
                }}
            >
                <Typography variant="body1" component="p" mb={3}>
                    {t(getTKey("intro.para1"))
                        .split(studyLink.display)
                        .map((part, i, arr) => (
                            <span key={i}>
                                {part}
                                {i < arr.length - 1 && (
                                    <Link color="secondary" href={studyLink.href}>
                                        {studyLink.display}
                                    </Link>
                                )}
                            </span>
                        ))}
                </Typography>
                <Typography
                    fontWeight={700}
                    variant="body1"
                    component="p"
                    mb={3}
                    tx="license.intro.para2"
                />

                <ol style={{ listStyle: "none" }}>
                    {/* 1) Definitions */}
                    <Typography component="li" variant="body1" mb={2}>
                        1) <u>{t(getTKey("sections.definitions.title"))}</u>.{" "}
                        {t(getTKey("sections.definitions.intro"))}
                    </Typography>
                    <ol style={{ listStyle: "none", paddingLeft: 24 }}>
                        <Typography component="li" variant="body1" mb={2}>
                            a) {t(getTKey("sections.definitions.licensee"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            b){" "}
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: t(getTKey("sections.definitions.site")),
                                }}
                            />
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            c) {t(getTKey("sections.definitions.authorizedUser"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            d) {t(getTKey("sections.definitions.licensedData"))}
                        </Typography>
                    </ol>

                    {/* 2) Authorized Use */}
                    <Typography component="li" variant="body1" mb={2}>
                        2) <u>{t(getTKey("sections.authorizedUse.title"))}</u>.{" "}
                        {t(getTKey("sections.authorizedUse.content"))}
                    </Typography>

                    {/* 3) Access to LICENSED DATA */}
                    <Typography component="li" variant="body1" mb={2}>
                        3) <u>{t(getTKey("sections.access.title"))}</u>.{" "}
                        {t(getTKey("sections.access.content"))
                            .split(studyLink.display)
                            .map((part, i, arr) => (
                                <span key={i}>
                                    {part}
                                    {i < arr.length - 1 && (
                                        <Link color="secondary" href={studyLink.href}>
                                            {studyLink.display}
                                        </Link>
                                    )}
                                </span>
                            ))}
                    </Typography>

                    {/* 4) Restrictions on Use */}
                    <Typography component="li" variant="body1" mb={2}>
                        4) <u>{t(getTKey("sections.restrictions.title"))}</u>.
                    </Typography>
                    <ol style={{ listStyle: "none", paddingLeft: 24 }}>
                        <Typography component="li" variant="body1" mb={2}>
                            a) {t(getTKey("sections.restrictions.a"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            b) {t(getTKey("sections.restrictions.b"))}
                        </Typography>
                        <ol style={{ listStyle: "none", paddingLeft: 24 }}>
                            <Typography component="li" variant="body1" mb={2}>
                                i) {t(getTKey("sections.restrictions.b1"))}
                            </Typography>
                            <Typography component="li" variant="body1" mb={2}>
                                ii) {t(getTKey("sections.restrictions.b2"))}
                            </Typography>
                            <Typography component="li" variant="body1" mb={2}>
                                iii) {t(getTKey("sections.restrictions.b3"))}
                            </Typography>
                            <Typography component="li" variant="body1" mb={2}>
                                iv) {t(getTKey("sections.restrictions.b4"))}
                            </Typography>
                            <Typography component="li" variant="body1" mb={2}>
                                v) {t(getTKey("sections.restrictions.b5"))}
                            </Typography>
                            <Typography component="li" variant="body1" mb={2}>
                                vi) {t(getTKey("sections.restrictions.b6"))}
                            </Typography>
                            <Typography component="li" variant="body1" mb={2}>
                                vii) {t(getTKey("sections.restrictions.b7"))}
                            </Typography>
                        </ol>
                    </ol>

                    {/* 5) Terms and Termination */}
                    <Typography component="li" variant="body1" mb={2}>
                        5) <u>{t(getTKey("sections.termsAndTermination.title"))}</u>.
                    </Typography>
                    <ol style={{ listStyle: "none", paddingLeft: 24 }}>
                        <Typography component="li" variant="body1" mb={2}>
                            a) {t(getTKey("sections.termsAndTermination.a"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            b) {t(getTKey("sections.termsAndTermination.b"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            c){" "}
                            {t(getTKey("sections.termsAndTermination.c"))
                                .split(studyLink.display)
                                .map((part, i, arr) => (
                                    <span key={i}>
                                        {part}
                                        {i < arr.length - 1 && (
                                            <Link color="secondary" href={studyLink.href}>
                                                {studyLink.display}
                                            </Link>
                                        )}
                                    </span>
                                ))}
                        </Typography>
                    </ol>

                    {/* 6) Undertakings */}
                    <Typography component="li" variant="body1" mb={2}>
                        6) <u>{t(getTKey("sections.undertakings.title"))}</u>.{" "}
                        {t(getTKey("sections.undertakings.content"))}
                    </Typography>

                    {/* 7) Audit Rights */}
                    <Typography component="li" variant="body1" mb={2}>
                        7) <u>{t(getTKey("sections.auditRights.title"))}</u>.{" "}
                        {t(getTKey("sections.auditRights.content"))}
                    </Typography>

                    {/* 8) Disclaimer of Warranty */}
                    <Typography component="li" variant="body1" mb={2}>
                        8) <u>{t(getTKey("sections.disclaimer.title"))}</u>.{" "}
                        {t(getTKey("sections.disclaimer.content"))}
                    </Typography>

                    {/* 9) LICENSEE Warranties */}
                    <Typography component="li" variant="body1" mb={2}>
                        9) <u>{t(getTKey("sections.licenseeWarranties.title"))}</u>.
                    </Typography>
                    <ol style={{ listStyle: "none", paddingLeft: 24 }}>
                        <Typography component="li" variant="body1" mb={2}>
                            a) {t(getTKey("sections.licenseeWarranties.a"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            b) {t(getTKey("sections.licenseeWarranties.b"))}
                        </Typography>
                    </ol>

                    {/* 10) Limitation of liability; Indemnity */}
                    <Typography component="li" variant="body1" mb={2}>
                        10) <u>{t(getTKey("sections.liability.title"))}</u>.
                    </Typography>
                    <ol style={{ listStyle: "none", paddingLeft: 24 }}>
                        <Typography component="li" variant="body1" mb={2}>
                            a) {t(getTKey("sections.liability.a"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            b) {t(getTKey("sections.liability.b"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            c) {t(getTKey("sections.liability.c"))}
                        </Typography>
                        <Typography component="li" variant="body1" mb={2}>
                            d){" "}
                            {t(getTKey("sections.liability.d"))
                                .split(studyLink.display)
                                .map((part, i, arr) => (
                                    <span key={i}>
                                        {part}
                                        {i < arr.length - 1 && (
                                            <Link color="secondary" href={studyLink.href}>
                                                {studyLink.display}
                                            </Link>
                                        )}
                                    </span>
                                ))}
                        </Typography>
                    </ol>

                    {/* 11) Trademarks */}
                    <Typography component="li" variant="body1" mb={2}>
                        11) <u>{t(getTKey("sections.trademarks.title"))}</u>.{" "}
                        {t(getTKey("sections.trademarks.content"))}
                    </Typography>

                    {/* 12) Governing Law */}
                    <Typography component="li" variant="body1" mb={2}>
                        12) <u>{t(getTKey("sections.governingLaw.title"))}</u>.{" "}
                        {t(getTKey("sections.governingLaw.content"))}
                    </Typography>

                    {/* 13) Integration and Severance */}
                    <Typography component="li" variant="body1" mb={2}>
                        13) <u>{t(getTKey("sections.integration.title"))}</u>.{" "}
                        {t(getTKey("sections.integration.content"))}
                    </Typography>
                </ol>
            </DialogContent>
            <Stack useFlexGap gap={2} px={3}>
                <Typography
                    align="center"
                    variant="body2"
                    component="p"
                    tx="license.footer.acceptance"
                />
                <Typography align="center" variant="body2" component="p">
                    {t(getTKey("footer.print"))
                        .split("<u>")
                        .map((part, i) => {
                            if (i === 0) return part;
                            const [underlined, rest] = part.split("</u>");
                            return (
                                <span key={i}>
                                    <u style={{ cursor: "pointer" }} onClick={() => window.print()}>
                                        {underlined}
                                    </u>
                                    {rest}
                                </span>
                            );
                        })}
                </Typography>
            </Stack>
            <DialogActions sx={{ justifyContent: "center", p: 3, pt: 2 }}>
                <Button sx={{ textTransform: "none" }} onClick={onCancel} variant="text">
                    {t(getTKey("buttons.cancel"))}
                </Button>
                <Button onClick={onAgree} variant="contained">
                    {t(getTKey("buttons.accept"))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
